import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useSearchParams } from 'react-router-dom';

import Action from '@/components/Action';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';

import { InvitePayload } from '../utils/buildInvitePayload';

type OrderType = {
  id: string;
};

type Props = {
  order: OrderType;
  hideModal: () => void;
  hireMessage?: string | null;
  invitePayload?: InvitePayload | null;
  // GRAVY-10803. This wizard hires while placing the order, and graphy now
  // refuses a worker who lacks a badge the job requires. The order itself
  // still succeeds (partial_success), so without this the admin lands on
  // "Order placed!" with the worker silently absent from the shift — the same
  // "no warning" complaint the ticket was filed for, just with the opposite
  // outcome. These are the per-worker refusal strings from the response.
  staffingErrors?: string[];
};

const PostSubmitModal = ({
  order,
  hideModal,
  hireMessage,
  invitePayload,
  staffingErrors = [],
}: Props) => {
  const [searchParams] = useSearchParams();
  const isDebug = searchParams.get('debug') === 'true';

  return (
    <Modal
      disableClickout
      size={isDebug ? 'md' : 'xs'}
      title={`Order #${order.id} placed!`}
      wrapperBackground={true}
      onRequestClose={hideModal}
    >
      <Card.Section>
        <Stack vertical gap={24}>
          <Body>Your order #{order.id} was successfully placed.</Body>
          {hireMessage && <Body>{hireMessage}</Body>}
          {staffingErrors.length > 0 && (
            <Alert
              data-testid="post-submit-staffing-errors"
              description={staffingErrors.join(' ')}
              icon={faExclamationTriangle}
              status="warning"
              title={
                staffingErrors.length === 1
                  ? 'One worker was not hired'
                  : `${staffingErrors.length} workers were not hired`
              }
            />
          )}
          {isDebug && invitePayload && (
            <div>
              <Small weight="medium">
                Debug: Worker invite/hire details (included in order creation)
              </Small>
              <pre
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '300px',
                  fontSize: '12px',
                  marginTop: '8px',
                }}
              >
                {JSON.stringify(invitePayload, null, 2)}
              </pre>
            </div>
          )}
          <Stack justify="end">
            <Button
              a11yLabel="Start a new order"
              appearance="outline"
              id="start-new-order"
              label="Place another"
              onClick={hideModal}
            />
            <Action.Button
              action={{
                a11yLabel: 'Navigate to order page',
                id: 'view-order-btn',
                label: 'View order',
                to: `/orders/${order.id}`,
              }}
              appearance="primary"
            />
          </Stack>
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default PostSubmitModal;
